/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo, useState } from "react";

import type { FormikHelpers } from "formik";
import { Formik, ErrorMessage, FieldArray, useField } from "formik";
import {
  Plus,
  Info,
  FlagIcon,
  CalendarClock,
  ChevronsDown,
  ChevronsUp,
  AlertTriangle,
} from "lucide-react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Spinner,
  UncontrolledTooltip,
  Collapse,
  Alert,
} from "reactstrap";
import * as Yup from "yup";

import {
  useGetAssignmentQuery,
  useCreateAssignmentMilestoneMutation,
  useCreateDeliverableMutation,
} from "app/apiSlice";
import Loader from "components/Loader";
import { showToast } from "components/Toasts/helpers/showToast";
import checkIfGracePeriod from "helpers/checkIfGracePeriod";

// import MultiImageUploader from "components/MultiImageUploader";

// Wrapper component to handle nested image uploads in form arrays
interface DeliverableImageUploaderProps {
  index: number;
  disabled?: boolean;
}

// TODO: #ui-update - the CSS is weird when using this replacement for ImageUploader which did not work with the deliverables nesting
const DeliverableImageUploader = ({
  index,
  disabled = false,
}: DeliverableImageUploaderProps) => {
  // Get the field references with the correct nested paths
  const imageUrlsFieldName = `deliverables[${index}].imageUrls`;
  const uploadedImagesFieldName = `deliverables[${index}].uploadedImages`;

  const [imageUrlsField] = useField(imageUrlsFieldName);
  const [uploadedImagesField] = useField(uploadedImagesFieldName);
  const [, , imageUrlsHelpers] = useField(imageUrlsFieldName);
  const [, , uploadedImagesHelpers] = useField(uploadedImagesFieldName);

  return (
    <div className="image-uploader">
      <Label className="text-muted">Screenshot(s)</Label>
      <div className="thumbnails">
        {/* Display existing imageUrls */}
        {imageUrlsField.value?.map((image: string, imgIndex: number) => (
          <div className="thumbnail" key={`existing-${imgIndex}`}>
            <img
              src={image}
              alt="Thumbnail of uploaded content"
              className="img-fluid"
            />
            {!disabled && (
              <button
                type="button"
                className="remove-image-btn"
                aria-label="Remove image"
                title="Remove image"
                onClick={() => {
                  const newImageUrls = [...imageUrlsField.value];
                  newImageUrls.splice(imgIndex, 1);
                  imageUrlsHelpers.setValue(newImageUrls);
                }}
              >
                X
              </button>
            )}
          </div>
        ))}

        {/* Display uploaded images */}
        {uploadedImagesField.value?.map((image: File, imgIndex: number) => (
          <div className="thumbnail" key={`uploaded-${imgIndex}`}>
            <img
              src={URL.createObjectURL(image)}
              alt="Thumbnail of uploaded content"
              className="img-fluid"
            />
            {!disabled && (
              <button
                type="button"
                className="remove-image-btn"
                aria-label="Remove image"
                title="Remove image"
                onClick={() => {
                  const newUploadedImages = [...uploadedImagesField.value];
                  newUploadedImages.splice(imgIndex, 1);
                  uploadedImagesHelpers.setValue(newUploadedImages);
                }}
              >
                X
              </button>
            )}
          </div>
        ))}

        {/* Upload button */}
        {!disabled && (
          <button
            type="button"
            aria-label="Upload images"
            title="Upload images"
            onClick={() => {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = "image/*";
              input.multiple = true;
              input.onchange = (e: any) => {
                try {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    const fileArray = Array.from(files);
                    const newVal = [
                      ...(uploadedImagesField.value || []),
                      ...fileArray,
                    ];
                    uploadedImagesHelpers.setValue(newVal);
                  }
                } catch (error) {
                  console.error("Error uploading image:", error);
                  showToast({
                    type: "error",
                    message: "Error uploading image",
                  });
                }
              };
              input.click();
            }}
            className="upload-photo-btn"
          >
            +
          </button>
        )}
      </div>
    </div>
  );
};

type DeliverableFormValues = {
  name: string;
  network: string;
  postUrl: string;
  handle: string;
  postDate: string;
  isComplete: boolean;
  images?: string[];
  imageUrls?: string[];
  uploadedImages?: any[];
};

// New component for Deliverable form section
interface DeliverableFormSectionProps {
  values: {
    deliverables: DeliverableFormValues[];
  };
  errors: any;
  touched: any;
  handleChange: (e: React.ChangeEvent<any>) => void;
  isAnyLoading: boolean;
  isLoadingCreateDeliverable: boolean;
  isMilestoneComplete: boolean;
}

const DeliverableFormSection = ({
  values,
  errors,
  touched,
  handleChange,
  isAnyLoading,
  isLoadingCreateDeliverable,
  isMilestoneComplete,
}: DeliverableFormSectionProps) => {
  // Keep the expanded deliverables state in this component
  const [expandedDeliverables, setExpandedDeliverables] = useState<
    Record<number, boolean>
  >({});

  // Toggle function for deliverable expansion
  const toggleDeliverable = (index: number) => {
    setExpandedDeliverables((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <>
      <ModalHeader className="border-top bg-body-secondary">
        <div className="d-flex align-items-center">
          <CalendarClock size={"1rem"} className="me-2" />
          Create Deliverables
          <span id="deliverablesInfoTooltip" className="ms-2 text-muted">
            <Info size={".9rem"} />
          </span>
          <UncontrolledTooltip target="deliverablesInfoTooltip">
            You may add deliverables directly in this form instead of adding
            them to the milestone separately later.
          </UncontrolledTooltip>
          {isLoadingCreateDeliverable && <Spinner size="sm" className="ms-2" />}
        </div>
      </ModalHeader>
      <ModalBody>
        {/* Warning message when milestone is complete */}
        {isMilestoneComplete && (
          <Alert color="info" className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <AlertTriangle size="1.2rem" className="me-2" />
              <strong>
                Deliverables Cannot Be Added to Completed Milestones
              </strong>
            </div>
            <p className="mb-0 mt-2">
              You've marked the above milestone as complete. Deliverables cannot
              be added to completed milestones. If you want to add deliverables
              to this milestone, please:
            </p>
            <ol className="mb-0 mt-2">
              <li>Uncheck "Completed?" in the milestone section above</li>
              <li>Create the milestone and deliverables together</li>
              <li>
                Make sure any incomplete deliverables are marked as complete
              </li>
              <li>Edit the milestone to mark it as complete</li>
            </ol>
          </Alert>
        )}

        {/* Deliverables section */}
        <FieldArray name="deliverables">
          {({ push, remove }) => (
            <div>
              {isMilestoneComplete ? (
                <p className="text-muted">
                  No deliverables can be added to completed milestones.
                </p>
              ) : (
                <>
                  {values.deliverables.length === 0 ? (
                    <p className="text-muted">No deliverables entered yet.</p>
                  ) : (
                    values.deliverables.map((deliverable, index) => (
                      <Card key={index} className="mb-3">
                        <CardHeader className="d-flex justify-content-between align-items-center p-2">
                          <div className="d-flex align-items-center">
                            <span className="mb-0">
                              Deliverable {index + 1}
                            </span>
                          </div>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => remove(index)}
                            outline
                            disabled={isAnyLoading || isMilestoneComplete}
                          >
                            Remove
                          </Button>
                        </CardHeader>
                        <CardBody className="p-3">
                          {/* Always visible fields: title and platform */}
                          <Row>
                            <Col md={8}>
                              <FormGroup>
                                <Label htmlFor={`deliverables[${index}].name`}>
                                  Title*
                                </Label>
                                <Input
                                  id={`deliverables[${index}].name`}
                                  name={`deliverables[${index}].name`}
                                  type="text"
                                  value={deliverable.name || ""}
                                  onChange={handleChange}
                                  invalid={
                                    !!(
                                      (errors.deliverables as any)?.[index]
                                        ?.name &&
                                      (touched.deliverables as any)?.[index]
                                        ?.name
                                    )
                                  }
                                  disabled={isAnyLoading || isMilestoneComplete}
                                />
                                <ErrorMessage
                                  name={`deliverables[${index}].name`}
                                  component="div"
                                  className="text-danger"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={4}>
                              <FormGroup>
                                <Label
                                  htmlFor={`deliverables[${index}].network`}
                                >
                                  Platform*
                                </Label>
                                <Input
                                  id={`deliverables[${index}].network`}
                                  name={`deliverables[${index}].network`}
                                  type="select"
                                  value={deliverable.network || ""}
                                  onChange={handleChange}
                                  invalid={
                                    !!(
                                      (errors.deliverables as any)?.[index]
                                        ?.network &&
                                      (touched.deliverables as any)?.[index]
                                        ?.network
                                    )
                                  }
                                  disabled={isAnyLoading || isMilestoneComplete}
                                >
                                  <option value="">Select...</option>
                                  <option value="instagram">Instagram</option>
                                  <option value="tiktok">TikTok</option>
                                  <option value="youtube">YouTube</option>
                                </Input>
                                <ErrorMessage
                                  name={`deliverables[${index}].network`}
                                  component="div"
                                  className="text-danger"
                                />
                              </FormGroup>
                            </Col>
                          </Row>

                          {/* Collapsible additional fields */}
                          <Collapse isOpen={!!expandedDeliverables[index]}>
                            <Row>
                              <Col md={6}>
                                <FormGroup>
                                  <Label
                                    htmlFor={`deliverables[${index}].postUrl`}
                                  >
                                    Post URL
                                  </Label>
                                  <Input
                                    id={`deliverables[${index}].postUrl`}
                                    name={`deliverables[${index}].postUrl`}
                                    type="text"
                                    value={deliverable.postUrl || ""}
                                    onChange={handleChange}
                                    disabled={
                                      deliverable.isComplete ||
                                      isAnyLoading ||
                                      isMilestoneComplete
                                    }
                                  />
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup>
                                  <Label
                                    htmlFor={`deliverables[${index}].handle`}
                                  >
                                    Handle
                                  </Label>
                                  <Input
                                    id={`deliverables[${index}].handle`}
                                    name={`deliverables[${index}].handle`}
                                    type="text"
                                    value={deliverable.handle || ""}
                                    onChange={handleChange}
                                    disabled={
                                      deliverable.isComplete ||
                                      isAnyLoading ||
                                      isMilestoneComplete
                                    }
                                  />
                                </FormGroup>
                              </Col>
                            </Row>

                            <Row>
                              <Col md={6}>
                                <FormGroup>
                                  <Label
                                    htmlFor={`deliverables[${index}].postDate`}
                                  >
                                    Date Posted
                                  </Label>
                                  <Input
                                    id={`deliverables[${index}].postDate`}
                                    name={`deliverables[${index}].postDate`}
                                    type="date"
                                    value={deliverable.postDate || ""}
                                    onChange={handleChange}
                                    disabled={
                                      deliverable.isComplete ||
                                      isAnyLoading ||
                                      isMilestoneComplete
                                    }
                                    invalid={
                                      !!(
                                        (errors.deliverables as any)?.[index]
                                          ?.postDate &&
                                        (touched.deliverables as any)?.[index]
                                          ?.postDate
                                      )
                                    }
                                  />
                                  <ErrorMessage
                                    name={`deliverables[${index}].postDate`}
                                    component="div"
                                    className="text-danger"
                                  />
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup check className="mt-4">
                                  <Label check>
                                    <Input
                                      id={`deliverables[${index}].isComplete`}
                                      name={`deliverables[${index}].isComplete`}
                                      type="checkbox"
                                      checked={deliverable.isComplete || false}
                                      onChange={handleChange}
                                      invalid={
                                        !!(
                                          (errors.deliverables as any)?.[index]
                                            ?.isComplete &&
                                          (touched.deliverables as any)?.[index]
                                            ?.isComplete
                                        )
                                      }
                                      disabled={
                                        isAnyLoading || isMilestoneComplete
                                      }
                                    />
                                    Completed?
                                  </Label>
                                  <ErrorMessage
                                    name={`deliverables[${index}].isComplete`}
                                    component="div"
                                    className="text-danger"
                                  />
                                </FormGroup>
                              </Col>
                            </Row>

                            <Row>
                              <Col>
                                <FormGroup>
                                  <DeliverableImageUploader
                                    index={index}
                                    disabled={
                                      deliverable.isComplete ||
                                      isAnyLoading ||
                                      isMilestoneComplete
                                    }
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                          </Collapse>

                          {/* Toggle button for additional fields */}
                          <div className="mt-2 mb-2">
                            <Button
                              color="link"
                              size="sm"
                              className="link-more p-0"
                              onClick={() => toggleDeliverable(index)}
                            >
                              {expandedDeliverables[index] ? (
                                <span>
                                  <ChevronsUp size=".9rem" className="me-1" />
                                  Hide additional deliverable fields
                                </span>
                              ) : (
                                <span>
                                  <ChevronsDown size=".9rem" className="me-1" />
                                  Show more deliverable fields
                                </span>
                              )}
                            </Button>
                          </div>
                        </CardBody>
                      </Card>
                    ))
                  )}
                </>
              )}

              <div className="mt-3 mb-3">
                <Button
                  color="primary"
                  outline
                  type="button"
                  className="d-flex align-items-center"
                  onClick={() =>
                    push({
                      name: "",
                      network: "",
                      postUrl: "",
                      handle: "",
                      postDate: "",
                      isComplete: false,
                      images: [],
                      imageUrls: [],
                      uploadedImages: [],
                    })
                  }
                  disabled={isAnyLoading || isMilestoneComplete}
                >
                  <Plus size={"1rem"} className="me-2" />
                  Deliverable
                </Button>
              </div>
            </div>
          )}
        </FieldArray>
      </ModalBody>
    </>
  );
};

type MilestoneFormValues = {
  name: string;
  priority: "high" | "normal" | "low";
  rate: number;
  updateAssignmentRate?: boolean;
  status: string;
  nextPaymentDate: string;
  isComplete: boolean;
  dateCompleted: string;
  releasePayment: boolean;
  isPaymentApproved: boolean;
  backdateCompletedDate?: boolean;
  overrideSegmentationSplit: boolean;
  dueDate: string;
  paymentTerm: "net60" | "net45" | "net30" | "other";
  deliverables: DeliverableFormValues[];
};

// New component for Milestone form section
interface MilestoneFormSectionProps {
  values: MilestoneFormValues;
  initialValues: MilestoneFormValues;
  errors: any;
  touched: any;
  handleChange: (e: React.ChangeEvent<any>) => void;
  isAnyLoading: boolean;
  isLoadingCreateMilestone: boolean;
  remainingBalance: number;
  toggle: () => void;
}

const MilestoneFormSection = ({
  values,
  initialValues,
  errors,
  touched,
  handleChange,
  isAnyLoading,
  isLoadingCreateMilestone,
  remainingBalance,
  toggle,
}: MilestoneFormSectionProps) => {
  return (
    <>
      <ModalHeader toggle={toggle} className="bg-body-secondary">
        <div className="d-flex align-items-center">
          <FlagIcon size={"1rem"} className="me-2" />
          Create Milestone
          {isLoadingCreateMilestone && <Spinner size="sm" className="ms-2" />}
        </div>
      </ModalHeader>
      <ModalBody>
        {isAnyLoading && <Loader />}

        <Row>
          <Col md={8}>
            <FormGroup>
              <Label for="name">Title</Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={values.name}
                onChange={handleChange}
                invalid={!!(errors.name && touched.name)}
                disabled={isAnyLoading}
              />
              <ErrorMessage
                name="name"
                component="div"
                className="text-danger"
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label for="priority">Priority</Label>
              <Input
                id="priority"
                name="priority"
                type="select"
                value={values.priority}
                onChange={handleChange}
                disabled={isAnyLoading}
              >
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </Input>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col>
            <FormGroup>
              <Label for="status">Status/Notes</Label>
              <Input
                id="status"
                name="status"
                type="text"
                value={values.status}
                onChange={handleChange}
                disabled={isAnyLoading}
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="rate">Rate</Label>
              <Input
                id="rate"
                name="rate"
                type="number"
                value={values.rate}
                onChange={handleChange}
                invalid={!!(errors.rate && touched.rate)}
                disabled={isAnyLoading}
              />
              <ErrorMessage
                name="rate"
                component="div"
                className="text-danger"
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="paymentTerm">Payment Terms</Label>
              <Input
                id="paymentTerm"
                name="paymentTerm"
                type="select"
                value={values.paymentTerm}
                onChange={handleChange}
                disabled={isAnyLoading}
              >
                <option value="net60">Net 60</option>
                <option value="net45">Net 45</option>
                <option value="net30">Net 30</option>
                <option value="other">Custom</option>
              </Input>
              <ErrorMessage
                name="paymentTerm"
                component="div"
                className="text-danger"
              />
            </FormGroup>
          </Col>
          {values.paymentTerm === "other" && (
            <Col md={6}>
              <FormGroup>
                <Label for="nextPaymentDate">Next Payment Date</Label>
                <Input
                  id="nextPaymentDate"
                  name="nextPaymentDate"
                  type="date"
                  value={values.nextPaymentDate}
                  onChange={handleChange}
                  disabled={isAnyLoading}
                />
                <ErrorMessage
                  name="nextPaymentDate"
                  component="div"
                  className="text-danger"
                />
              </FormGroup>
            </Col>
          )}
        </Row>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="dueDate">Due Date</Label>
              <Input
                id="dueDate"
                name="dueDate"
                type="date"
                value={values.dueDate}
                onChange={handleChange}
                disabled={isAnyLoading}
              />
              <ErrorMessage
                name="dueDate"
                component="div"
                className="text-danger"
              />
            </FormGroup>
          </Col>
        </Row>

        {remainingBalance < values.rate && (
          <Row className="mb-3">
            <Col>
              <FormGroup check>
                <ErrorMessage
                  name="updateAssignmentRate"
                  component="div"
                  className="text-danger"
                />
                <Label check>
                  <Input
                    id="updateAssignmentRate"
                    name="updateAssignmentRate"
                    type="checkbox"
                    checked={values.updateAssignmentRate}
                    onChange={handleChange}
                    disabled={isAnyLoading}
                  />
                  Update Assignment Rate?
                </Label>
              </FormGroup>
            </Col>
          </Row>
        )}

        <Row className="mb-3">
          <Col md={12}>
            <FormGroup check>
              <ErrorMessage
                name="overrideSegmentationSplit"
                component="div"
                className="text-danger"
              />
              <Label check for="overrideSegmentationSplit">
                <Input
                  id="overrideSegmentationSplit"
                  name="overrideSegmentationSplit"
                  type="checkbox"
                  checked={values.overrideSegmentationSplit}
                  onChange={handleChange}
                  disabled={isAnyLoading}
                />
                Reimbursement Milestone?
              </Label>
            </FormGroup>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={4}>
            <FormGroup check>
              <Label check>
                <Input
                  id="isComplete"
                  name="isComplete"
                  type="checkbox"
                  checked={values.isComplete}
                  onChange={handleChange}
                  disabled={values.releasePayment || isAnyLoading}
                />
                Completed?
              </Label>
            </FormGroup>
          </Col>
          {(values.isComplete || values.dateCompleted) && (
            <Col md={8}>
              <FormGroup>
                <Label for="dateCompleted">Date Completed</Label>
                <Input
                  id="dateCompleted"
                  name="dateCompleted"
                  type="date"
                  value={values.dateCompleted}
                  onChange={handleChange}
                  disabled={values.releasePayment || isAnyLoading}
                />
                <ErrorMessage
                  name="dateCompleted"
                  component="div"
                  className="text-danger"
                />
              </FormGroup>
              {values.dateCompleted &&
                !checkIfGracePeriod(values.dateCompleted) && (
                  <FormGroup check>
                    <Label check>
                      <Input
                        id="backdateCompletedDate"
                        name="backdateCompletedDate"
                        type="checkbox"
                        checked={values.backdateCompletedDate || false}
                        onChange={handleChange}
                        disabled={isAnyLoading}
                      />
                      I understand this completion date is outside the grace
                      period and will trigger historical accounting adjustments.
                    </Label>
                    <ErrorMessage
                      name="backdateCompletedDate"
                      component="div"
                      className="text-danger"
                    />
                  </FormGroup>
                )}
            </Col>
          )}
        </Row>

        <Row className="mb-3">
          <Col>
            <FormGroup check>
              <Label check>
                <Input
                  id="releasePayment"
                  name="releasePayment"
                  type="checkbox"
                  checked={values.releasePayment}
                  onChange={handleChange}
                  disabled={
                    !values.isComplete || !values.dateCompleted || isAnyLoading
                  }
                />
                Release Payment?
              </Label>
              <ErrorMessage
                name="releasePayment"
                component="div"
                className="text-danger"
              />
            </FormGroup>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <FormGroup check>
              <Label check>
                <Input
                  id="isPaymentApproved"
                  name="isPaymentApproved"
                  type="checkbox"
                  checked={values.isPaymentApproved}
                  onChange={handleChange}
                  disabled={!values.releasePayment || isAnyLoading}
                />
                Payment Approved?
              </Label>
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
    </>
  );
};

interface CreateMilestoneModalProps {
  assignment_id: string;
  isOpen: boolean;
  onClose: () => void;
}

export const CreateMilestoneModal = ({
  assignment_id,
  isOpen,
  onClose,
}: CreateMilestoneModalProps) => {
  const {
    data: assignment,
    error: assignmentError,
    isLoading: isLoadingAssignment,
  } = useGetAssignmentQuery(
    { _id: assignment_id },
    {
      skip: !assignment_id,
      refetchOnMountOrArgChange: true,
    },
  );

  const [createMilestone, { isLoading: isLoadingCreateMilestone }] =
    useCreateAssignmentMilestoneMutation();

  const [createDeliverable, { isLoading: isLoadingCreateDeliverable }] =
    useCreateDeliverableMutation();

  // Overall loading state
  const isAnyLoading =
    isLoadingCreateMilestone ||
    isLoadingAssignment ||
    isLoadingCreateDeliverable;

  const initialValues: MilestoneFormValues = {
    name: "",
    priority: "normal",
    rate: 0,
    updateAssignmentRate: false,
    status: "",
    nextPaymentDate: "",
    isComplete: false,
    dateCompleted: "",
    releasePayment: false,
    isPaymentApproved: false,
    backdateCompletedDate: false,
    deliverables: [],
    dueDate: "",
    paymentTerm: "net60",
    overrideSegmentationSplit: false,
  };

  const validationSchema = useMemo(() => {
    if (!assignment) return Yup.object();

    return Yup.object({
      name: Yup.string().required("Title is required"),
      priority: Yup.string().oneOf(
        ["high", "normal", "low"],
        "Invalid priority",
      ),
      rate: Yup.number()
        .required("Rate is required")
        .moreThan(0, "Must be a positive number"),
      updateAssignmentRate: Yup.boolean().test(
        "check-assignment-rate",
        "Milestone would exceed remaining assignment rate. Decrease Milestone rate or increase Assignment rate.",
        function (updateAssignmentRate) {
          const { rate } = this.parent;

          if (!assignment?.milestones) return true;

          const milestonesBalance = assignment.milestones.reduce(
            (acc: number, milestone: any) => acc + milestone.rate,
            0,
          );
          const remainingBalance = assignment.rate - milestonesBalance;

          // If rate exceeds remaining balance and not updating assignment rate, fail validation
          return !(remainingBalance < (rate || 0) && !updateAssignmentRate);
        },
      ),
      status: Yup.string(),
      // Custom validation to require at least one date field
      dueDate: Yup.string().test(
        "at-least-one-date-required",
        "At least one of Due Date, Date Completed, or Next Payment Date is required",
        function (dueDate) {
          const { dateCompleted, nextPaymentDate } = this.parent;

          // Check if at least one of the three date fields has a value
          const hasDueDate = !!dueDate;
          const hasDateCompleted = !!dateCompleted;
          const hasNextPaymentDate = !!nextPaymentDate;

          return hasDueDate || hasDateCompleted || hasNextPaymentDate;
        },
      ),
      nextPaymentDate: Yup.string()
        .when("paymentTerm", {
          is: "other",
          then: (schema) => schema.required("Next payment date is required"),
          otherwise: (schema) => schema,
        })
        .test(
          "at-least-one-date-required",
          "At least one of Due Date, Date Completed, or Next Payment Date is required",
          function (nextPaymentDate) {
            const { dueDate, dateCompleted } = this.parent;

            // Check if at least one of the three date fields has a value
            const hasDueDate = !!dueDate;
            const hasDateCompleted = !!dateCompleted;
            const hasNextPaymentDate = !!nextPaymentDate;

            return hasDueDate || hasDateCompleted || hasNextPaymentDate;
          },
        ),
      paymentTerm: Yup.string().required("Payment term is required"),
      overrideSegmentationSplit: Yup.boolean(),
      isComplete: Yup.boolean(),
      dateCompleted: Yup.string()
        .when("isComplete", {
          is: true,
          then: (schema) => schema.required("Completion date is required"),
          otherwise: (schema) => schema,
        })
        .test(
          "not-future-date",
          "Completion date cannot be in the future.",
          function (dateCompleted) {
            if (!dateCompleted) return true;

            const completionDate = new Date(dateCompleted);
            const today = new Date();

            return completionDate <= today;
          },
        )
        .test(
          "at-least-one-date-required",
          "At least one of Due Date, Date Completed, or Next Payment Date is required",
          function (dateCompleted) {
            const { dueDate, nextPaymentDate } = this.parent;

            // Check if at least one of the three date fields has a value
            const hasDueDate = !!dueDate;
            const hasDateCompleted = !!dateCompleted;
            const hasNextPaymentDate = !!nextPaymentDate;

            return hasDueDate || hasDateCompleted || hasNextPaymentDate;
          },
        ),
      backdateCompletedDate: Yup.boolean().test(
        "require-confirmation-when-outside-grace",
        "Confirmation required when completion date is outside grace period.",
        function (backdateCompletedDate) {
          const { dateCompleted } = this.parent as { dateCompleted?: string };
          if (!dateCompleted) return true;
          const isWithinGrace = checkIfGracePeriod(dateCompleted);
          return isWithinGrace || !!backdateCompletedDate;
        },
      ),
      releasePayment: Yup.boolean()
        .test(
          "requires-complete",
          "Milestones can not be released for payment if they are incomplete.",
          function (releasePayment) {
            // Only validate if releasing payment
            if (!releasePayment) return true;

            const { isComplete } = this.parent;
            return isComplete === true;
          },
        )
        .test(
          "requires-date-completed",
          "Completion date is required to release payment",
          function (releasePayment) {
            // Only validate if releasing payment
            if (!releasePayment) return true;

            const { dateCompleted } = this.parent;
            return !!dateCompleted;
          },
        ),
      isPaymentApproved: Yup.boolean().test(
        "requires-release-payment",
        "Payment can only be approved if it has been released for payment.",
        function (isPaymentApproved) {
          if (!isPaymentApproved) return true;

          const { releasePayment } = this.parent;
          return releasePayment === true;
        },
      ),
      // Validation for deliverables array - skip validation if milestone is complete
      deliverables: Yup.array().when("isComplete", {
        is: true,
        then: (schema) => schema, // No validation when milestone is complete
        otherwise: (schema) =>
          schema.of(
            Yup.object().shape({
              name: Yup.string().required("Title is required"),
              network: Yup.string().required("Platform is required"),
              postUrl: Yup.string(),
              handle: Yup.string(),
              postDate: Yup.string().test(
                "not-future-date",
                "Date cannot be in the future.",
                function (date) {
                  if (!date) return true;
                  const postDate = new Date(date);
                  const today = new Date();
                  return postDate <= today;
                },
              ),
              isComplete: Yup.boolean().test(
                "has-required-fields",
                "Deliverables can only be completed if either a post URL or screenshot, handle, and date posted is provided.",
                function (isComplete) {
                  if (!isComplete) return true;

                  const {
                    postUrl,
                    handle,
                    postDate,
                    imageUrls,
                    uploadedImages,
                  } = this.parent;
                  const hasImages =
                    imageUrls?.length > 0 || uploadedImages?.length > 0;

                  return !!postUrl || !!(hasImages && handle && postDate);
                },
              ),
            }),
          ),
      }),
    });
  }, [assignment]);

  const handleSubmit = useCallback(
    async (
      values: MilestoneFormValues,
      actions: FormikHelpers<MilestoneFormValues>,
    ) => {
      try {
        if (!assignment_id) {
          showToast({
            type: "error",
            message: "Error creating milestone - no assignment ID",
          });
          return;
        }

        // Extract deliverables from form values
        const { deliverables, ...milestoneValues } = values;

        // Create the milestone first
        const result = await createMilestone({
          assignment_id,
          milestone: milestoneValues,
        }).unwrap();
        // TODO: need to get milestone that is nested in the assignment result. should be something like result.result.milestones[index]._id
        // The issue though is we don't know for sure which milestone in the array is the one we just created.
        // We need to match that up somehow to get the milestone ID from the response
        // Skip deliverable creation if milestone is marked as complete
        if (
          deliverables &&
          deliverables.length > 0 &&
          !milestoneValues.isComplete
        ) {
          let milestone_id: string | undefined;

          if (
            result?.result?.milestones &&
            Array.isArray(result.result.milestones)
          ) {
            // Find all milestones that match our basic criteria
            const matchingMilestones = result.result.milestones.filter(
              (m: any) =>
                // TODO: get loginUser from wherever
                // m.createdByAdmin_id === currentUser?._id &&
                m.name === milestoneValues.name &&
                m.rate === milestoneValues.rate &&
                m.priority === milestoneValues.priority,
            );

            if (matchingMilestones.length > 0) {
              // If there's only one match, use it directly. Else If there are multiple matches, take the most recently created one
              if (matchingMilestones.length === 1) {
                milestone_id = matchingMilestones[0]._id;
              } else {
                const sortedMilestones = [...matchingMilestones].sort(
                  (a, b) => {
                    if (a.created && b.created) {
                      return (
                        new Date(b.created).getTime() -
                        new Date(a.created).getTime()
                      );
                    }
                    return 0;
                  },
                );

                // Take the first one (most recently created, or first match if no created field)
                milestone_id = sortedMilestones[0]._id;

                // Log a warning if there were multiple matches
                console.warn(
                  `Found ${matchingMilestones.length} matching milestones. Using the most recently created one.`,
                );
              }
            } else {
              // No matching milestone found - this is an error
              throw new Error(
                "Could not identify the newly created milestone. The milestone was likely created but deliverables cannot be attached.",
              );
            }
          } else {
            // No milestones array available - this is an error
            throw new Error(
              "Response from server is missing milestones data. The milestone was likely created but deliverables cannot be attached.",
            );
          }

          if (!milestone_id) {
            throw new Error("Failed to get milestone ID from response");
          }

          // Create all deliverables in parallel and track results

          // Prepare all deliverable creation promises
          const deliverablePromises = deliverables.map((deliverable, index) => {
            // Extract the uploadedImages for proper API call structure
            const { uploadedImages, ...deliverableRest } = deliverable;

            // Return a promise that resolves with the index and result or rejects with the index and error
            return createDeliverable({
              ...deliverableRest,
              images: uploadedImages || [],
              milestone_id,
              assignment_id,
            })
              .unwrap()
              .then((result) => ({
                status: "fulfilled",
                index,
                name: deliverable.name || `Deliverable ${index + 1}`,
                value: result,
              }))
              .catch((error) => ({
                status: "rejected",
                index,
                name: deliverable.name || `Deliverable ${index + 1}`,
                reason: error,
              }));
          });

          // Wait for all promises to settle (complete or fail)
          const deliverablesResults = await Promise.all(deliverablePromises);

          // Separate successful and failed results
          const deliverablesSuccessful = deliverablesResults.filter(
            (result) => result.status === "fulfilled",
          );
          const deliverablesFailed = deliverablesResults.filter(
            (result) => result.status === "rejected",
          ) as any[];

          // Create dynamic success message
          let successMessage = "Milestone created successfully";
          if (deliverablesSuccessful.length > 0) {
            successMessage += ` with ${deliverablesSuccessful.length} deliverable${deliverablesSuccessful.length !== 1 ? "s" : ""}`;
          }

          // Show appropriate toast messages
          if (deliverablesFailed.length === 0) {
            // All succeeded
            showToast({
              type: "success",
              message: successMessage,
            });
          } else if (deliverablesSuccessful.length > 0) {
            // Some succeeded, some failed
            showToast({
              type: "warning",
              message: `${successMessage}, but ${deliverablesFailed.length} deliverable${deliverablesFailed.length !== 1 ? "s" : ""} failed to save.`,
            });

            // Show which ones failed if there are just a few
            if (deliverablesFailed.length <= 3) {
              deliverablesFailed.forEach((failure) => {
                showToast({
                  type: "error",
                  message: `Failed to save "${failure.name}": ${failure.reason?.data?.message || failure.reason?.message || "Unknown error"}`,
                });
              });
            } else {
              // Just show a summary for many failures
              showToast({
                type: "error",
                message: `Failed deliverables: ${deliverablesFailed.map((f) => f.name).join(", ")}`,
              });
            }
          } else {
            // All failed
            showToast({
              type: "error",
              message: `Milestone created, but all ${deliverablesFailed.length} deliverables failed to save.`,
            });
          }
        } else {
          showToast({
            type: "success",
            message: "Milestone created successfully",
          });
        }

        onClose();
      } catch (error: any) {
        console.error({ error });

        showToast({
          type: "error",
          message: `Error saving milestone. ${error?.data?.message || error?.message || error}`,
        });
      } finally {
        actions.setSubmitting(false);
      }
    },
    [assignment_id, createMilestone, createDeliverable, onClose],
  );

  if (assignmentError) {
    const message = "Error opening milestone form";
    showToast({
      type: "error",
      message,
    });
    console.error({ message, assignmentError });
    return null;
  }

  // Calculate if the milestones already exceed the assignment rate
  const milestonesBalance =
    assignment?.milestones?.reduce(
      (acc, milestone) => acc + milestone.rate,
      0,
    ) || 0;
  const remainingBalance = (assignment?.rate || 0) - milestonesBalance;

  return (
    <Modal isOpen={isOpen} toggle={onClose} backdrop="static" size="lg">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur={true}
        validateOnChange={false}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          isSubmitting,
          errors,
          touched,
        }) => (
          <Form onSubmit={handleSubmit}>
            <div className="milestone-section">
              <MilestoneFormSection
                values={values}
                initialValues={initialValues}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                isAnyLoading={isAnyLoading}
                isLoadingCreateMilestone={isLoadingCreateMilestone}
                remainingBalance={remainingBalance}
                toggle={onClose}
              />
            </div>
            <div className="deliverables-section">
              <DeliverableFormSection
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                isAnyLoading={isAnyLoading}
                isLoadingCreateDeliverable={isLoadingCreateDeliverable}
                isMilestoneComplete={values.isComplete}
              />
            </div>

            <ModalFooter>
              <Button
                color="secondary"
                onClick={onClose}
                disabled={isSubmitting || isAnyLoading}
              >
                Cancel
              </Button>
              <Button
                color="dark"
                type="submit"
                disabled={isSubmitting || isAnyLoading}
              >
                Create
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default CreateMilestoneModal;
